import type { EChartsOption } from 'echarts'

import {
  formatFieldValue,
  toNumber,
  type ChartSeries,
  type Granularity,
  type ReportField,
} from '@/lib/cube-report'
import { formatInteger, formatTimeBucket } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { ReportRow } from '@/services/cube/report'

interface TimeSeriesDef {
  /** Zaman ekseni; yoksa `category` boyutu kategori eksenidir. */
  time?: string
  category?: string
  series: ChartSeries[]
  fields: Map<string, ReportField>
  breakdown?: string
}

/**
 * Kırılım değerleri (ör. firmalar) ayırt edilebilsin diye ayrı tonlu kategorik palet;
 * `chart-1…5` tek renk rampası, yan yana dilimlerde birbirine karışır (DESIGN.md §7).
 */
const BREAKDOWN_COLORS = [
  '#4e79a7',
  '#f28e2b',
  '#59a14f',
  '#e15759',
  '#b07aa1',
  '#76b7b2',
  '#edc948',
  '#9c755f',
]

/** Kırılımda ayrı dilim sayısı (palet boyu); fazlası tek "Diğer" diliminde toplanır. */
export const MAX_BREAKDOWN_GROUPS = BREAKDOWN_COLORS.length

/** Kırılımlı grafikte ilk ölçü düz, sonrakiler çapraz taralı çizilir (renk kırılım değerinde). */
const STRIPES = {
  color: 'rgba(255, 255, 255, 0.55)',
  dashArrayX: [1, 0],
  dashArrayY: [3, 3],
  rotation: -Math.PI / 4,
}

type AxisDef = Pick<TimeSeriesDef, 'time' | 'category' | 'series' | 'breakdown'>

/** Satırın eksen kovası: zaman ekseninde `time.granularity`, kategoride boyutun değeri. */
export function bucketKey(def: AxisDef, granularity: Granularity): (row: ReportRow) => string {
  const member = def.time ? `${def.time}.${granularity}` : (def.category as string)
  return (row) => {
    const value = row[member]
    return value === null || value === undefined ? '' : String(value)
  }
}

/**
 * Kategori adı (banka hesabı) uzun olabilir: dar ekranda çizim alanını ezmesin diye
 * kısaltılır, tam ad ipucunda. Zaman etiketleri kısa, dokunulmaz.
 */
function categoryAxisLabel(def: AxisDef) {
  return def.time ? {} : { axisLabel: { width: 140, overflow: 'truncate' as const } }
}

/** Yatay grafiğin değer ekseni: dar ekranda çakışan rakamlar gizlenir. */
const valueAxisLabel = {
  formatter: (value: number) => formatInteger(value),
  hideOverlap: true,
}

function bucketLabel(def: AxisDef, granularity: Granularity): (key: string) => string {
  return def.time
    ? (key) => formatTimeBucket(key, granularity)
    : (key) => key || messages.filters.column.blank
}

/**
 * Grafiğin kategori ekseni: kova başına ilk satır (tık/seçim `dataIndex`'i bu diziye göredir).
 * Zaman ekseninde zaman sırasıyla (kırılımsız sorguda satırların kendisi); kategori ekseninde
 * ölçü toplamı büyükten küçüğe, toplamı sıfır olan kova atılır.
 */
export function chartBuckets(
  rows: ReportRow[],
  def: AxisDef,
  granularity: Granularity,
): ReportRow[] {
  if (def.time && !def.breakdown) return rows
  const keyOf = bucketKey(def, granularity)
  const seen = new Map<string, { row: ReportRow; total: number }>()
  for (const row of rows) {
    const sum = def.series.reduce((acc, item) => acc + (toNumber(row[item.measure]) ?? 0), 0)
    const bucket = seen.get(keyOf(row))
    if (bucket) bucket.total += sum
    else seen.set(keyOf(row), { row, total: sum })
  }
  const buckets = [...seen.values()]
  if (def.time) return buckets.map((bucket) => bucket.row)
  return buckets
    .filter((bucket) => bucket.total !== 0)
    .sort((a, b) => b.total - a.total)
    .map((bucket) => bucket.row)
}

function escapeHtml(text: string): string {
  return text.replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]!,
  )
}

/**
 * Zaman kovaları üzerinde ölçü serileri (grafik raporu ve pano grafiği).
 * `horizontal`: kovalar dikey eksende, yukarıdan aşağı zaman sırasıyla; sağ eksen yok.
 */
export function buildTimeSeriesOption(
  def: TimeSeriesDef,
  rows: ReportRow[],
  granularity: Granularity,
  horizontal = false,
  /** Çapraz filtrede seçili kova anahtarları (`bucketKey`); varsa diğerleri soluklaşır. */
  selected?: ReadonlySet<string>,
): EChartsOption {
  if (def.breakdown) {
    return buildBreakdownOption(def, def.breakdown, rows, granularity, horizontal, selected)
  }
  const field = (member: string) => def.fields.get(member) as ReportField
  const axisName = (axis: 'left' | 'right') =>
    def.series
      .filter((series) => series.axis === axis)
      .map((series) => field(series.measure).label)
      .join(' / ')
  const hasRightAxis = !horizontal && def.series.some((series) => series.axis === 'right')
  const axisLabel = { formatter: (value: number) => formatInteger(value) }
  const keyOf = bucketKey(def, granularity)
  const label = bucketLabel(def, granularity)
  const buckets = chartBuckets(rows, def, granularity)
  const categories = {
    type: 'category' as const,
    data: buckets.map((row) => label(keyOf(row))),
  }
  const series = def.series.map((item) => ({
    name: field(item.measure).label,
    type: item.type,
    yAxisIndex: item.axis === 'right' && hasRightAxis ? 1 : 0,
    data: buckets.map((row) => {
      const value = row[item.measure] as number
      const dimmed = selected?.size && !selected.has(keyOf(row))
      return dimmed ? { value, itemStyle: { opacity: 0.35 } } : value
    }),
    tooltip: { valueFormatter: (value: unknown) => formatFieldValue(field(item.measure), value) },
  }))

  if (horizontal) {
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      legend: { bottom: 0 },
      grid: { left: 16, right: 40, top: 16, bottom: 40, containLabel: true },
      yAxis: { ...categories, ...categoryAxisLabel(def), inverse: true },
      xAxis: { type: 'value', axisLabel: valueAxisLabel },
      series: series.map(({ yAxisIndex: _unused, ...rest }) => rest),
    }
  }

  return {
    tooltip: { trigger: 'axis' },
    // ECharts 6'da legend varsayılan olarak altta; eksen etiketleriyle çakışmasın.
    legend: { bottom: 0 },
    grid: { left: 48, right: 48, top: 40, bottom: 56 },
    xAxis: categories,
    yAxis: [
      { type: 'value', name: axisName('left'), axisLabel },
      ...(hasRightAxis
        ? [
            {
              type: 'value' as const,
              name: axisName('right'),
              axisLabel,
              splitLine: { show: false },
            },
          ]
        : []),
    ],
    series,
  }
}

interface TooltipParam {
  seriesIndex: number
  seriesName: string
  marker: string
  value: number | { value: number }
  axisValueLabel: string
}

/**
 * Kırılımlı seriler: her ölçü bir yığın (`stack`), yığının dilimleri kırılım değerleri.
 * Aynı adlı seriler ECharts'ta aynı rengi alır ve göstergede tek öğe olur; böylece bir
 * firmayı göstergeden kapatmak onu her ölçüden çıkarır. İpucu ölçü başına toplam + dökümdür.
 */
function buildBreakdownOption(
  def: TimeSeriesDef,
  breakdown: string,
  rows: ReportRow[],
  granularity: Granularity,
  horizontal: boolean,
  selected?: ReadonlySet<string>,
): EChartsOption {
  const field = (member: string) => def.fields.get(member) as ReportField
  const bucketOf = bucketKey(def, granularity)
  const groupOf = (row: ReportRow) => {
    const value = row[breakdown]
    return value === null || value === undefined || value === ''
      ? messages.filters.column.blank
      : String(value)
  }
  const buckets = chartBuckets(rows, def, granularity).map(bucketOf)
  const totals = new Map<string, number>()
  for (const row of rows) {
    const group = groupOf(row)
    const sum = def.series.reduce((acc, item) => acc + (toNumber(row[item.measure]) ?? 0), 0)
    totals.set(group, (totals.get(group) ?? 0) + sum)
  }
  // Büyük dilim eksene yakın, göstergede de önde; palete sığmayanlar "Diğer"de.
  const ranked = [...totals.keys()].sort((a, b) => totals.get(b)! - totals.get(a)!)
  const other = messages.report.breakdownOther
  const overflow = ranked.length > MAX_BREAKDOWN_GROUPS
  const kept = new Set(overflow ? ranked.slice(0, MAX_BREAKDOWN_GROUPS - 1) : ranked)
  const groups = overflow ? [...kept, other] : ranked
  const cells = new Map<string, number>()
  for (const row of rows) {
    const group = kept.has(groupOf(row)) ? groupOf(row) : other
    for (const item of def.series) {
      const key = `${bucketOf(row)}\u0000${group}\u0000${item.measure}`
      cells.set(key, (cells.get(key) ?? 0) + (toNumber(row[item.measure]) ?? 0))
    }
  }
  const hasRightAxis = !horizontal && def.series.some((series) => series.axis === 'right')
  const axisLabel = { formatter: (value: number) => formatInteger(value) }

  const series = def.series.flatMap((item, index) =>
    groups.map((group) => ({
      name: group,
      type: 'bar' as const,
      stack: item.measure,
      ...(!horizontal && { yAxisIndex: item.axis === 'right' && hasRightAxis ? 1 : 0 }),
      ...(index > 0 && { itemStyle: { decal: STRIPES } }),
      data: buckets.map((bucket) => {
        const value = cells.get(`${bucket}\u0000${group}\u0000${item.measure}`) ?? 0
        return selected?.size && !selected.has(bucket)
          ? { value, itemStyle: { opacity: 0.35 } }
          : value
      }),
    })),
  )

  const tooltip = {
    trigger: 'axis' as const,
    axisPointer: { type: 'shadow' as const },
    formatter: (raw: unknown) => {
      const params = (Array.isArray(raw) ? raw : [raw]) as TooltipParam[]
      const valueOf = (param: TooltipParam) =>
        typeof param.value === 'object' ? param.value.value : param.value
      const lines = [
        `<div style="font-weight:600">${escapeHtml(params[0]?.axisValueLabel ?? '')}</div>`,
      ]
      def.series.forEach((item, index) => {
        const measure = field(item.measure)
        const own = params
          .filter((param) => Math.floor(param.seriesIndex / groups.length) === index)
          .filter((param) => valueOf(param) !== 0)
          .sort((a, b) => valueOf(b) - valueOf(a))
        const total = own.reduce((acc, param) => acc + valueOf(param), 0)
        lines.push(
          `<div style="margin-top:6px;display:flex;justify-content:space-between;gap:16px;font-weight:600">` +
            `<span>${escapeHtml(measure.label)}</span><span>${escapeHtml(formatFieldValue(measure, total))}</span></div>`,
        )
        for (const param of own) {
          lines.push(
            `<div style="display:flex;justify-content:space-between;gap:16px">` +
              `<span>${param.marker}${escapeHtml(param.seriesName)}</span>` +
              `<span>${escapeHtml(formatFieldValue(measure, valueOf(param)))}</span></div>`,
          )
        }
      })
      return lines.join('')
    },
  }
  const categories = {
    type: 'category' as const,
    data: buckets.map(bucketLabel(def, granularity)),
  }

  // Gösterge tek satır, kaydırmalı: uzun adlar (banka hesapları) çok satıra taşıp ekseni örtmesin.
  if (horizontal) {
    return {
      color: BREAKDOWN_COLORS,
      tooltip,
      legend: { bottom: 0, type: 'scroll', data: groups },
      grid: { left: 16, right: 40, top: 16, bottom: 40, containLabel: true },
      yAxis: { ...categories, ...categoryAxisLabel(def), inverse: true },
      xAxis: { type: 'value', axisLabel: valueAxisLabel },
      series,
    }
  }
  return {
    color: BREAKDOWN_COLORS,
    tooltip,
    legend: { bottom: 0, type: 'scroll', data: groups },
    grid: { left: 48, right: 48, top: 40, bottom: 56 },
    xAxis: categories,
    yAxis: [
      { type: 'value', axisLabel },
      ...(hasRightAxis ? [{ type: 'value' as const, axisLabel, splitLine: { show: false } }] : []),
    ],
    series,
  }
}
